import { useCallback } from 'react'

import type { PublicJob } from '@/types/public-share'
import { useAuth } from '@/hooks/use-auth'
import { useSavedOpportunities } from '@/hooks/use-saved-opportunities'
import { redirectToLogin } from '@/lib/auth-return'
import { cn } from '@/lib/utils'
import { MARKETING_STICKY_NAV_SCROLL_MT } from '@/landing/layouts/landing-layouts'

import { PublicOpenRoleCard } from '@/components/public/public-open-role-card'

type PublicOpenRolesSectionProps = {
  roles: PublicJob[]
  className?: string
  jobHref?: (job: PublicJob) => string
  jobLinkState?: (job: PublicJob) => unknown
}

export function PublicOpenRolesSection({
  roles,
  className,
  jobHref,
  jobLinkState,
}: PublicOpenRolesSectionProps) {
  const { isAuthenticated } = useAuth()
  const { savedIds, toggleSave, savingOpportunityId } = useSavedOpportunities()

  const handleSaveToggle = useCallback(
    (opportunityId: string) => {
      if (!isAuthenticated) {
        redirectToLogin()
        return
      }
      toggleSave(opportunityId, savedIds.has(opportunityId))
    },
    [isAuthenticated, savedIds, toggleSave],
  )

  if (roles.length === 0) return null

  return (
    <section
      id="open-roles"
      className={cn('space-y-4', MARKETING_STICKY_NAV_SCROLL_MT, className)}
    >
      <h2 className="text-xl font-semibold">Open roles</h2>
      <ul className="space-y-4">
        {roles.map((job) => (
          <li key={job.id}>
            <PublicOpenRoleCard
              job={job}
              saved={savedIds.has(job.id)}
              isSaving={savingOpportunityId === job.id}
              onSaveToggle={() => handleSaveToggle(job.id)}
              to={jobHref?.(job)}
              linkState={jobLinkState?.(job)}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}
